import type { MemberOption } from '@/components/ui/member-autocomplete'
import type { FeedbackPerson, FeedbackRequestStatus } from '@/types/feedback'

/** The API's limits (feedback.service.js LIMITS). */
export const FEEDBACK_LIMITS = Object.freeze({
  /** Plain text of a feedback, once the tags go. */
  bodyText: 5000,
  message: 1000
})

export const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number }; status?: number })?.response?.status ?? (err as { status?: number })?.status

export const messageOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message

/**
 * Plain text of the editor's HTML, as the API counts it: an "empty" editor
 * arrives as `<p></p>`, and an entity counts as one character.
 */
export const plainText = (html: string): string => String(html || '')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .replace(/&[a-z#0-9]+;/gi, 'x')
  .replace(/\s+/g, ' ')
  .trim()

export const toOption = (p: FeedbackPerson): MemberOption => ({
  id: p.id,
  name: p.name || '',
  role: p.role || 'member',
  tagline: p.tagline || undefined,
  jobTitle: p.jobTitle ?? null,
  avatar: p.avatar || undefined,
  type: 'user'
})

/** The API's `errors` array (`{ param, message }`), empty when absent. */
export const errorsOf = (err: unknown): { param?: string; message?: string }[] => {
  const list = (err as { response?: { data?: { errors?: unknown } } })?.response?.data?.errors
  return Array.isArray(list) ? list as { param?: string; message?: string }[] : []
}

/** The index of `giverIds[i]` in an error's `param`, or null. */
export const nomineeIndexOf = (param?: string): number | null => {
  const match = /^giverIds\[(\d+)\]$/.exec(param || '')
  return match ? Number(match[1]) : null
}

/** The status pill of a request. */
export const STATUS_STYLE: Record<FeedbackRequestStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-800',
  ANSWERED: 'bg-green-50 text-green-700',
  DECLINED: 'bg-gray-100 text-gray-600',
  CANCELED: 'bg-gray-100 text-gray-600'
}
